import { Component, Input } from '@angular/core';
import { openClose } from '@core/animations/animations';
import { LawFirm } from '@models/lawFirms';
import { LawFirmEvent } from '@models/lawFirms';
import { MatterBusinessUser } from '@models/matter';
import {
	MatterMortgage,
	MatterMortgageInstructionSource,
	MortgagePercentageOfInterestTypes,
	TransactionType,
} from '@models/mortgage';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import { ICON } from '@core/util/icon';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { Lender, LenderArgs } from '@models/lender';
import {
	LenderClientAssociation,
	LenderClientAssociationTypes,
} from '@components/lender-card/client-lender-card/client-lender-card.component';
import { AssociatedContactType } from '@models/clients';
import { MatDialog } from '@angular/material/dialog';
import { ChangePriorityComponent } from '../change-priority/change-priority.component';
import { ClientCreationService } from '@services/closer-api/client-creation/client-creation.service';

const InstructionSourceText = {
	[MatterMortgageInstructionSource.ASSYST]: 'Assyst',
	[MatterMortgageInstructionSource.LLC]: 'LLC',
	[MatterMortgageInstructionSource.EMAIL]: 'Email',
	[MatterMortgageInstructionSource.FAX]: 'Fax',
};

const InstructionSourceOptions: DropdownOption<MatterMortgageInstructionSource>[] =
	[
		{
			value: MatterMortgageInstructionSource.ASSYST,
			name: InstructionSourceText[MatterMortgageInstructionSource.ASSYST],
		},
		{
			value: MatterMortgageInstructionSource.LLC,
			name: InstructionSourceText[MatterMortgageInstructionSource.LLC],
		},
		{
			value: MatterMortgageInstructionSource.EMAIL,
			name: InstructionSourceText[MatterMortgageInstructionSource.EMAIL],
		},
		{
			value: MatterMortgageInstructionSource.FAX,
			name: InstructionSourceText[MatterMortgageInstructionSource.FAX],
		},
	];

@Component({
	selector: 'app-manual-mortgage-details',
	templateUrl: './mortgage-details.component.html',
	animations: [openClose()],
	standalone: false,
})
export class ManualMortgageDetailsComponent {
	public ICON = ICON;
	public showAddGuarantor = false;
	public _mortgage: MatterMortgage;
	public instructionSourceOptions = InstructionSourceOptions;
	public InstructionSourceText = InstructionSourceText;
	public selectedInstructionSourceText: string;
	public isVTBMortgage = false;
	public TransactionType = TransactionType;

	@ViewSelectSnapshot(SelectedMatter.assignedLawyer)
	assignedLawyer: MatterBusinessUser;

	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private matDialog: MatDialog,
		private clientCreationService: ClientCreationService,
	) {}

	@Input() set mortgage(_mortgage: MatterMortgage) {
		this._mortgage = _mortgage;
		this.selectedInstructionSourceText =
			this.InstructionSourceText[_mortgage.source];
		this.isVTBMortgage = _mortgage.transactionType === TransactionType.VTB;
	}

	setSource(
		$event: DropdownResult<DropdownOption<MatterMortgageInstructionSource>>,
	): void {
		this.mortgageDispatch.setSource(this._mortgage.id, $event.result.value);
	}

	setGeneralSecurityAgreement(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			generalSecurityAgreement: value,
		});
	}

	setAssignmentOfRents(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			assignmentOfRents: value,
		});
	}

	setAmount(value: number): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { amount: value });
	}

	setReferenceNumber(value: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			referenceNumber: value,
		});
	}

	setDealId(value: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { dealId: value });
	}

	setRemarks(value: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { remarks: value });
	}

	setCommitmentDate(date: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			commitmentDate: date,
		});
	}

	setStandardChargeTerm(value: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			standardChargeTerm: value,
		});
	}

	editPriority(): void {
		this.matDialog.open(ChangePriorityComponent);
	}

	onLenderRemoved(lenderId: string) {
		this.mortgageDispatch.RemoveMortgageLender(this._mortgage.id, lenderId);
		this.mortgageDispatch.SetMortgageChargeType(this._mortgage.id, null);
	}

	onLenderUpdated(lender: Lender) {
		this.mortgageDispatch.AddMortgageLender(this._mortgage.id, {
			lender: lender,
		});
	}

	onLenderAdded(lenderArgs: LenderArgs) {
		this.mortgageDispatch.AddMortgageLender(this._mortgage.id, lenderArgs);
	}

	onAddAssociation(association: LenderClientAssociation) {
		if (
			association.associationType ===
			LenderClientAssociationTypes.ASSOCIATED_CONTACT
		) {
			this.clientCreationService
				.AddExistingClientToAssociation({
					associatedContactType: AssociatedContactType.SIGNING_OFFICER,
					childClientId: association.associatingClientId,
					parentClientId: association.baseClientId,
					position: null,
				})
				.toPromise();
		}

		if (association.associationType === LenderClientAssociationTypes.INVESTOR) {
			this.mortgageDispatch.AddInvestorToLender(
				this._mortgage.id,
				association.baseClientId,
				association.associatingClientId,
			);
		}
	}

	onRemoveInvestor(result: { lenderId: string; investorId: string }) {
		this.mortgageDispatch.RemoveInvestorFromLender(
			this._mortgage.id,
			result.lenderId,
			result.investorId,
		);
	}

	onLenderSetAsPrimary(lenderId: string) {
		this.mortgageDispatch.SetMortgageLenderAsPrimary(
			this._mortgage.id,
			lenderId,
		);
	}

	onSolicitorUpdated(updatedSolicitor: LawFirm) {
		if (updatedSolicitor) {
			this.mortgageDispatch.UpdateCorrespondenceSolicitor(
				this._mortgage.id,
				updatedSolicitor,
				updatedSolicitor.leap?.personId,
				this.matterId,
				(updatedSolicitor as LawFirmEvent).role,
			);
		} else {
			this.mortgageDispatch.RemoveCorrespondence(this._mortgage.id);
		}
	}
	onCorrespondenceRemoved() {
		this.mortgageDispatch.RemoveCorrespondence(this._mortgage.id);
	}

	onPercentageOfInterestTypeUpdated(type: MortgagePercentageOfInterestTypes) {
		this.mortgageDispatch.SetPercentageOfInterestType(this._mortgage.id, type);
	}

	onLenderPercentageOfInterestUpdated(data: {
		lenderId: string;
		percentageOfInterest: string;
		percentageOfInterestType: MortgagePercentageOfInterestTypes;
	}) {
		this.mortgageDispatch.SetMortgageLenderPercentageOfInterestDetails(
			this._mortgage.id,
			data.lenderId,
			data.percentageOfInterest,
			data.percentageOfInterestType,
		);
	}
}
